/**
 * @file ClientSettingFieldNaming.js
 * @description This file contains the ClientSettingFieldNaming component, which is a reusable form for displaying and editing an client's field naming information.
 */

import { useState, useEffect, useRef } from 'react';
import {
    getClientOptions,
    createClientOption,
    updateClientOption,
} from '../../services/clientService';
import Toast from '../ui/toasts/Toast';
import { SecurityAttributes, ClientOptionEnum } from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import { Dropdown } from '../helpers/Dropdown';
import { getText } from '../../i18n';

const ClientSettingFieldNaming = () => {
    const didRun = useRef(false);

    const [initialFieldNamingSetting, setInitialFieldNamingSetting] = useState(
        {},
    );

    const [defaultFieldNaming, setDefaultFieldNaming] = useState();
    const fieldNamingJSON = JSON.parse(defaultFieldNaming ?? '[]');

    // Transform the array
    const transformedArray = fieldNamingJSON.map((item, index) => {
        // Each item is an object with a single key-value pair
        // Extract the value (label) from the object
        const label = Object.values(item)[0];
        return {
            id: index,
            label: label,
        };
    });

    // Sort the transformed array by label in ascending order
    const sortedArray = transformedArray.slice().sort((a, b) => {
        // Use localeCompare for proper alphabetical sorting
        return a.label.localeCompare(b.label);
    });

    const [pairs, setPairs] = useState([{ selectedId: null, inputText: '' }]);

    const handleDropdownChange = (index, selectedId) => {
        setPairs(prevPairs => {
            const newPairs = [...prevPairs];
            newPairs[index].selectedId = selectedId;
            return newPairs;
        });
    };

    const handleInputChange = (index, text) => {
        setPairs(prevPairs => {
            const newPairs = [...prevPairs];
            newPairs[index].inputText = text;
            return newPairs;
        });
    };

    const addNewRow = () => {
        setPairs(prevPairs => [
            ...prevPairs,
            { selectedId: null, inputText: '' }, // new empty pair
        ]);
    };

    const handleDeletePair = indexToDelete => {
        if (pairs.length === 1) setPairs([{ selectedId: null, inputText: '' }]);
        else
            setPairs(prevPairs =>
                prevPairs.filter((_, i) => i !== indexToDelete),
            );
    };

    const [isOpen, setIsOpen] = useState(true);
    const { hasAttribute, reloadFieldNaming } = useAuthContext();

    const getOptions = async () => {
        try {
            //GET
            const getData = await getClientOptions(0, [
                ClientOptionEnum.FieldNaming,
            ]);

            const fieldNamingObject = getData.find(
                item => item.name === 'Field Naming',
            );
            if (fieldNamingObject) {
                setInitialFieldNamingSetting(fieldNamingObject);
            } else {
                Toast.error(getText('msg_87ecd9e5ae7f'));
            }

            if (
                !fieldNamingObject.usingDefaultValue &&
                hasAttribute(SecurityAttributes.ClientSettingsFullAccess)
            ) {
                //GET DEFAULT
                const getDefaultData = await getClientOptions(
                    0,
                    [ClientOptionEnum.FieldNaming],
                    true,
                );
                const defaultFieldNamingObject = getDefaultData.find(
                    item => item.name === 'Field Naming',
                );
                setDefaultFieldNaming(defaultFieldNamingObject.value);

                setInitialPairs(
                    fieldNamingObject.value,
                    defaultFieldNamingObject.value,
                );
            } else {
                setDefaultFieldNaming(fieldNamingObject.value);
            }
        } catch (error) {
            console.error('Could not fetch:', error);
        }
    };

    useEffect(() => {
        if (!didRun.current) {
            getOptions();
            didRun.current = true;
        }
    }, []);

    const toggleAccordion = () => {
        setIsOpen(!isOpen);
    };

    const setInitialPairs = (fieldNamingValue, defaultFieldNamingValue) => {
        if (fieldNamingValue && defaultFieldNamingValue) {
            const fieldNamingArray = JSON.parse(fieldNamingValue);
            const defaultFieldNamingArray = JSON.parse(defaultFieldNamingValue);

            const defaultLabels = defaultFieldNamingArray.map(
                obj => Object.values(obj)[0],
            );

            // Map to include key and label
            const differencesWithKey = fieldNamingArray
                .map((obj, index) => {
                    const key = Object.keys(obj)[0];
                    const label = obj[key];
                    if (!defaultLabels.includes(label)) {
                        return { selectedId: index, inputText: label, key };
                    }
                    return null;
                })
                .filter(item => item !== null);

            // Sort by key (property name)
            differencesWithKey.sort((a, b) => a.key.localeCompare(b.key));

            // Map to final shape without key
            const differences = differencesWithKey.map(
                ({ selectedId, inputText }) => ({
                    selectedId,
                    inputText,
                }),
            );

            setPairs(
                differences.length > 0
                    ? differences
                    : [{ selectedId: null, inputText: '' }],
            );
        }
    };

    const updateSelectedFieldNaming = () => {
        const newFieldNamingJSON = [...fieldNamingJSON];
        const seenLabels = new Set();

        pairs.forEach((pair, index) => {
            // Defensive check: ensure index is valid
            if (
                index < 0 ||
                index >= newFieldNamingJSON.length ||
                pair.selectedId === null
            ) {
                return;
            }

            if (!pair.inputText) {
                throw new Error('Label must be populated.');
            }

            // Check for duplicate inputText
            if (seenLabels.has(pair.inputText)) {
                throw new Error('Label must be unique for the client');
            }
            seenLabels.add(pair.inputText);

            // Each item is an object with a single key-value pair
            // Extract the key (e.g. "CustomText1")
            const key = Object.keys(newFieldNamingJSON[pair.selectedId])[0];

            // Update the label (value) for that key
            newFieldNamingJSON[pair.selectedId][key] = pair.inputText;
        });

        return JSON.stringify(newFieldNamingJSON);
    };

    const handleSave = async () => {
        let noChanges = true;
        let updatedFieldNaming = '';
        try {
            updatedFieldNaming = updateSelectedFieldNaming();
        } catch (error) {
            return Toast.error(
                error.message || 'Label field must be populated.',
            );
        }

        if (initialFieldNamingSetting.usingDefaultValue) {
            //make a post
            noChanges = false;
            const option = {
                clientOptionId: initialFieldNamingSetting.clientOptionId,
                value: updatedFieldNaming,
            };
            const response = await createClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_64e3130f2003'));
            setInitialFieldNamingSetting(response.data);
        } else if (initialFieldNamingSetting.value != updatedFieldNaming) {
            //make a put
            noChanges = false;
            const option = {
                clientOptionId: initialFieldNamingSetting.clientOptionId,
                value: updatedFieldNaming,
            };
            const response = await updateClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_eb8283539512'));
            setInitialFieldNamingSetting(response.data);
        }

        if (noChanges) Toast.warning(getText('msg_13c3a7ac4e47'));
        else {
            await reloadFieldNaming();
            Toast.success(getText('msg_785229303df2'));
        }
    };

    return (
        <div
            style={{
                overflow: 'hidden',
                borderRadius: '12px',
                border: 'solid 1px rgba(47, 43, 67, 0.2)',
                marginTop: '10px',
                boxShadow:
                    'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px',
            }}
        >
            <div
                style={{
                    overflowY: 'auto',
                    padding: '16px',
                    resize: 'none',
                    boxShadow:
                        'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px;',
                }}
            >
                <div>
                    <div style={{ marginLeft: '12px' }}>
                        {getText('msg_1115f23a96f1')}
                        <i
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color3)',
                                display: 'flex',
                                float: 'inline-end',
                                cursor: 'pointer',
                            }}
                            className={
                                'bi bi-chevron-' + (isOpen ? 'up' : 'down')
                            }
                            onClick={() => toggleAccordion()}
                        />
                    </div>
                </div>
                {isOpen && (
                    <div
                        style={{
                            marginLeft: '12px',
                            marginTop: '12px',
                            color: 'var(--iNGAGED-Neutral-Color2)',
                        }}
                    >
                        <div style={{ marginTop: '12px' }}>
                            {getText('msg_e8b61aca53f5')}
                        </div>

                        <table
                            style={{
                                width: '100%',
                                marginBottom: '1rem',
                                marginTop: '36px',
                                verticalAlign: 'top',
                            }}
                            className="no-hover"
                        >
                            <thead>
                                <tr className="cursor-text">
                                    <th style={{ width: '40%' }}>
                                        {getText('msg_c326a4660b67')}
                                    </th>
                                    <th style={{ width: '40%' }}>
                                        {getText('msg_74341e3c271d')}
                                    </th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pairs.map((pair, index) => {
                                    // Collect all selected IDs except the current pair's selectedId
                                    const selectedIdsExceptCurrent = pairs
                                        .filter((_, i) => i !== index)
                                        .map(p => p.selectedId)
                                        .filter(id => id !== null);

                                    // Filter options to exclude those selected in other pairs
                                    const filteredOptions = sortedArray.filter(
                                        option =>
                                            !selectedIdsExceptCurrent.includes(
                                                option.id,
                                            ),
                                    );
                                    return (
                                        <tr
                                            className="cursor-default"
                                            style={
                                                index === 0
                                                    ? {
                                                          borderTop:
                                                              '12px solid transparent',
                                                      }
                                                    : {}
                                            }
                                        >
                                            <td
                                                style={{
                                                    padding: '0px 10px 0px 0px',
                                                }}
                                                className="cursor-pointer"
                                            >
                                                <Dropdown
                                                    defaultValue={
                                                        transformedArray[
                                                            pair.selectedId
                                                        ]?.label ?? 'Select'
                                                    }
                                                    options={filteredOptions}
                                                    onChange={value =>
                                                        handleDropdownChange(
                                                            index,
                                                            value,
                                                        )
                                                    }
                                                    useOnChangeValueFallback={
                                                        true
                                                    }
                                                />
                                            </td>
                                            <td style={{ padding: '0px' }}>
                                                <input
                                                    type="text"
                                                    maxLength="40"
                                                    className={`form-control`}
                                                    placeholder={getText(
                                                        'msg_37a9003af786',
                                                    )}
                                                    value={pair.inputText}
                                                    onChange={e =>
                                                        handleInputChange(
                                                            index,
                                                            e.target.value,
                                                        )
                                                    }
                                                    required
                                                />
                                            </td>
                                            <td>
                                                <i
                                                    className="bi bi-trash me-2 cursor-pointer"
                                                    style={{ color: 'red' }}
                                                    onClick={() =>
                                                        handleDeletePair(index)
                                                    }
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {pairs.length < sortedArray.length && (
                            <div
                                style={{
                                    color: 'var(--iNGAGED-Primary-Color)',
                                    cursor: 'pointer',
                                    width: 'fit-content',
                                }}
                                onClick={addNewRow}
                            >
                                {getText('msg_5e9eb8cca733')}
                            </div>
                        )}
                        {hasAttribute(
                            SecurityAttributes.ClientSettingsFullAccess,
                        ) && (
                            <div
                                className={`d-flex justify-content-evenly mt-5`}
                            >
                                <button
                                    type="button"
                                    onClick={() =>
                                        setInitialPairs(
                                            initialFieldNamingSetting.value,
                                            defaultFieldNaming,
                                        )
                                    }
                                    className="ing-popup-blue-negative"
                                >
                                    {getText('msg_77dfd2135f4d')}
                                </button>
                                <button
                                    type="button"
                                    className="ing-button-primary-none"
                                    onClick={handleSave}
                                >
                                    {getText('msg_efc007a393f6')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientSettingFieldNaming;
